import { DoorOpen, DoorClosed, RulerIcon, Home } from "lucide-react";

/**
 * iter135 — Gate Width picker.
 *
 * Displayed on the Estimate + Book flow so the crew knows whether the
 * mower fits through the customer's backyard gate before arriving.
 *
 * Options (backend canonical keys):
 *   • no_gate   — "No Gate"
 *   • under_36  — "Under 36\""
 *   • 36_to_48  — "36\"–48\""
 *   • over_48   — "Over 48\""
 *
 * Value contract: string (one of the keys above) or `""` when unset.
 */
export const GATE_WIDTH_OPTIONS = [
  { key: "no_gate",  label: "No Gate",      sub: "Open backyard access",       icon: DoorOpen },
  { key: "under_36", label: 'Under 36"',    sub: "Push mower only",           icon: DoorClosed },
  { key: "36_to_48", label: '36"–48"',      sub: "Fits most mid-size mowers", icon: RulerIcon },
  { key: "over_48",  label: 'Over 48"',     sub: "Fits any mower",            icon: Home },
];

export function GateWidthPicker({ value, onChange, disabled = false, required = true }) {
  return (
    <div data-testid="gate-width-picker" className="space-y-2.5">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] font-black tracking-[0.14em] uppercase text-emerald-900/60">
          Gate Width{required ? " *" : ""}
        </div>
        {!value && required && (
          <div className="text-[11px] text-amber-700 font-semibold" data-testid="gate-width-required">
            Please select an option
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {GATE_WIDTH_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.key)}
              data-testid={`gate-width-${opt.key}`}
              className={`text-left rounded-2xl border-2 p-3 transition-all ${
                active
                  ? "bg-gradient-to-br from-emerald-800 to-emerald-900 border-emerald-900 text-white shadow-lg"
                  : "bg-white border-slate-200 hover:border-emerald-400 hover:shadow-sm text-slate-900"
              } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
            >
              <div className="flex items-start justify-between">
                <Icon className={`w-5 h-5 ${active ? "text-emerald-100" : "text-emerald-700"}`} />
                {active && (
                  <span className="text-[10px] font-black tracking-wider bg-white/25 rounded px-1.5 py-0.5">
                    ✓
                  </span>
                )}
              </div>
              <div className={`mt-2 text-sm font-bold ${active ? "text-white" : "text-slate-900"}`}>
                {opt.label}
              </div>
              <div className={`text-[11px] ${active ? "text-emerald-100" : "text-slate-500"}`}>
                {opt.sub}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function gateWidthLabel(key) {
  const opt = GATE_WIDTH_OPTIONS.find((o) => o.key === key);
  return opt ? opt.label : "";
}

export default GateWidthPicker;
